// Buscar por nombre, por dueño o por cédula.
//
// ── Los acentos ──
//
// Nadie escribe «Peña» ni «Meléndez» con tilde en una caja de búsqueda, y menos
// desde un teléfono en una finca. Un buscador que exige la tilde exacta falla
// justo con los apellidos que la llevan — y quien lo usa concluye que la finca
// no está, no que escribió distinto.
//
// Por eso se comparan las dos cosas sin tildes: lo que se escribe y lo que está
// guardado. «pena» encuentra «Peña», y «Peña» también.
//
// ── Los puntos de la cédula ──
//
// La misma cédula se guarda «71.204.885» y se busca «71204885», o al revés. Se
// quitan los puntos, las comas, los guiones y los espacios de los DOS lados
// antes de comparar, y así da igual cómo se haya escrito cada vez.

// Quita tildes y pasa a minúscula. `normalize('NFD')` separa la letra de su
// tilde, y el rango ̀-ͯ son las tildes ya sueltas.
const plano = (v) => String(v == null ? '' : v)
  .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// Solo dígitos y letras: para comparar documentos escritos de mil formas.
const soloAlfa = (v) => plano(v).replace(/[^a-z0-9]/g, '');

// ¿Este registro coincide con lo buscado?
//
// `campos` son los valores donde se busca. Cualquiera que contenga lo escrito
// vale: quien busca «esperanza» no sabe si eso es el nombre de la finca o el
// apellido del dueño, y no tiene por qué saberlo.
export function coincide(q, campos) {
  const b = plano(q);
  // Una caja con solo signos —«.», «---»— es lo mismo que una caja vacía: no
  // se ha buscado nada todavía.
  //
  // Y hay que decidirlo aquí. Un punto suelto SÍ casa con «71.204.885» por
  // simple coincidencia de texto, así que sin este renglón escribir «.»
  // devolvía una finca al azar —la que tuviera puntos en la cédula— y eso se
  // lee como que el buscador está roto.
  if (!b || !soloAlfa(q)) return true;
  const bNum = soloAlfa(q);
  for (const c of campos || []) {
    if (plano(c).includes(b)) return true;
    // El número, comparado sin puntos ni guiones. Solo si lo buscado tiene
    // algo que comparar: con la caja en «.» no puede casar todo.
    if (bNum && soloAlfa(c).includes(bNum)) return true;
  }
  return false;
}

// Las entregas de leche que coinciden.
//
// ── Por qué hace falta ──
//
// Quien manda la información se equivoca, y hay que corregir. Con doscientas
// entregas anotadas, encontrar la de ayer de SANTA ELENA bajando con el dedo no
// se puede — y la tabla ni siquiera las enseña todas.
//
// Se busca por la finca, por la nota y por la FECHA escrita como la dice la
// gente: «22 de ago», «ago», «22/08». Nadie va a escribir 2026-08-22.
//
// Y por los litros y el precio, porque a veces lo que se recuerda del renglón
// equivocado es la cifra: «el de 220 litros».
export function filtrarRecibos(recibos, q, comoSeLee) {
  if (!plano(q) || !soloAlfa(q)) return recibos || [];
  return (recibos || []).filter(r => coincide(q, [
    r.finca, r.nota,
    // La fecha en los dos idiomas: como se guarda y como se lee.
    String(r.fecha || '').slice(0, 10),
    comoSeLee ? comoSeLee(r.fecha) : null,
    // Sin decimales de más: quien busca «220» no escribe «220.5».
    r.litros != null ? String(Math.round(r.litros)) : null,
    r.precio_litro != null ? String(Math.round(r.precio_litro)) : null,
    r.total != null ? String(Math.round(r.total)) : null,
  ]));
}

// Las fincas que coinciden. Se busca por el nombre de la finca, por el dueño,
// por la cédula y por el teléfono.
//
// Por teléfono también porque a veces lo que se tiene a la mano es el número
// que llamó, no el nombre.
export function filtrarFincas(fincas, q) {
  if (!plano(q) || !soloAlfa(q)) return fincas || [];
  return (fincas || []).filter(f =>
    coincide(q, [f.nombre, f.propietario, f.documento, f.telefono, f.ubicacion]));
}
